import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { BOSSES, KINGDOMS } from '@yuan-math/content';
import type { GradeResult } from '@yuan-math/exercises';
import {
  battleReducer,
  bossCleared,
  conceptsToReview,
  currentQuestion,
  replayBattle,
  type BattleQuestion,
  type BattleState,
  type BossDef,
} from '@yuan-math/world';
import { Button, Card, Confetti, Mascot } from '@yuan-math/ui';
import { ExerciseRunner } from '../../components/ExerciseRunner.tsx';
import { MasteryBand } from '../../components/MasteryBand.tsx';
import { kingdomSealed } from '../../components/kingdomSeal.ts';
import { bank, registry, useLearner } from '../../state/LearnerContext.tsx';
import { useRewards } from '../../state/RewardsContext.tsx';
import { NotFoundPage } from '../NotFoundPage.tsx';

/**
 * `/adventure/boss/:bossId` — the fight (docs/rpg.md §15).
 *
 * **The battle is derived, not stored.** Everything on this screen —
 * the question, the tally, the dots, the stage, whether the fight is won — is
 * rebuilt on every render by `replayBattle` from three things: who the student
 * is, which attempt this is, and the ordered list of how each answer went. The
 * profile keeps only the last two. That is what makes a reloaded fight show
 * the question it was showing a second ago, and it is why there is no
 * `useState` here holding a battle: a copy of the fight in React state is a
 * copy that can disagree with the profile the moment anything else touches it.
 *
 * **Every answer counts twice, and neither is optional.** A boss question is
 * generated by the ordinary bank and graded by the ordinary grader, so it is
 * real evidence: it goes through `recordAttempt` exactly as a practice answer
 * does, moving mastery, XP, the streak and the daily quests. It also goes to
 * `recordBattleAnswer`, which is the fight's own record. Feeding only the battle
 * would mean a student could fight a dragon for ten minutes and have the
 * platform record that they did nothing (see the integration note in
 * `packages/world/src/boss.ts`).
 *
 * **Malformed input is not a wrong answer** (`CLAUDE.md`). `ExerciseRunner`
 * never reports one, the check below refuses one anyway, and `battleReducer`
 * and `applyAttempt` both refuse one after that. Losing a dragon fight to a
 * typo is the most effective way there is to teach a child to fear the box.
 *
 * **Losing costs nothing** (docs/rpg.md §12). There is no HP, no lost gold, no
 * broken streak and nothing stored about a failed attempt. A defeat produces
 * one thing — the concepts the wrong answers touched, as links — and the retry
 * is the next attempt, with different problems by construction.
 *
 * **No timers.** Nothing here is on a clock. A boss that hurried a child would
 * be measuring their nerve rather than their mathematics.
 */

/* -------------------------------------------------------------------------- */
/* The dots                                                                   */
/* -------------------------------------------------------------------------- */

/** One question of the current stage, as it went. */
type DotOutcome = 'correct' | 'wrong' | 'pending';

const DOT_GLYPH: Record<DotOutcome, string> = { correct: '✓', wrong: '✕', pending: '·' };
const DOT_WORD: Record<DotOutcome, string> = { correct: 'right', wrong: 'wrong', pending: 'not answered yet' };

/**
 * How the current stage is going, one dot per question.
 *
 * Colour is never the only carrier (`CLAUDE.md`): each dot is a colour *and* a
 * glyph *and* a word in its own label, so the row reads the same to somebody
 * who cannot tell the green from the red, and reads at all to a screen reader.
 *
 * There is no timer, no clock and nothing counting down beside it. The dots
 * report what has happened and say nothing about what is left to do in it.
 */
function StageDots({ outcomes }: { outcomes: DotOutcome[] }): JSX.Element {
  const answered = outcomes.filter((outcome) => outcome !== 'pending').length;
  return (
    <ol className="boss-dots" aria-label={`Stage progress: ${answered} of ${outcomes.length} answered`}>
      {outcomes.map((outcome, index) => (
        <li
          key={index}
          className={`boss-dot boss-dot--${outcome} ${index === answered ? 'boss-dot--current' : ''}`}
        >
          <span aria-hidden="true">{DOT_GLYPH[outcome]}</span>
          <span className="visually-hidden">{`Question ${index + 1}: ${DOT_WORD[outcome]}`}</span>
        </li>
      ))}
    </ol>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function BossPage(): JSX.Element {
  const { bossId } = useParams();
  const {
    profile,
    masteryOf,
    recordAttempt,
    recordBattleAnswer,
    resetBattle,
    settleBossVictory,
  } = useLearner();
  const { celebrate } = useRewards();

  const def: BossDef | undefined = BOSSES.find((candidate) => candidate.id === bossId);

  /*
   * The fight is derived from a fixed moment rather than from `Date.now()` on
   * every render. `startBattle` records `now` and never seeds from it, so this
   * only keeps `startedAt` still — but a value that moved every render would be
   * a new object every render, and the memo below would never hold.
   */
  const [openedAt] = useState(() => Date.now());

  const record = profile?.rpg?.battles?.[def?.id ?? ''];
  const attemptIndex = record?.attemptIndex ?? 0;
  const answers = useMemo(() => record?.answers ?? [], [record?.answers]);

  const battle = useMemo(
    () => (def && profile ? replayBattle(def, bank, profile.id, attemptIndex, answers, openedAt) : undefined),
    [def, profile?.id, attemptIndex, answers, openedAt],
  );

  /*
   * The story is skippable in one tap (docs/rpg.md §21), and a student who is
   * already mid-fight never sees it again: it opens closed once an answer has
   * been given, which is also what a reload lands on.
   */
  const [reading, setReading] = useState(() => answers.length === 0);

  /**
   * The question being *shown*, when it is no longer the question being asked.
   *
   * The answer is recorded the moment it is graded — so a reload cannot undo a
   * miss, and nobody can read the explanation, reload and answer again — but
   * the explanation underneath it is the whole teaching moment, and swapping
   * the question out from under it would throw that away. So the answered
   * question is held on screen until the student presses on, while the fight
   * behind it has already moved.
   *
   * It is also what a *second* try is recorded against: the fight has moved on
   * and may even be over, but the student is still working on this question,
   * and an answer given to it is evidence about this concept.
   */
  const [held, setHeld] = useState<BattleQuestion | null>(null);

  /**
   * Which cleared stage the student has pressed on from.
   *
   * The pause between stages is a moment, not a state of the fight: the boss
   * changing form is worth a beat, and nothing about it is worth persisting.
   * `live` below is the fight with that beat taken.
   */
  const [advancedStage, setAdvancedStage] = useState<number | null>(null);

  /** Whether this visit is the one that beat the boss, for the confetti. */
  const [firstClearHere, setFirstClearHere] = useState(false);

  /**
   * The fight as the screen has it: the derived battle, plus the one thing the
   * screen knows that the profile does not — whether the interlude has been
   * dismissed. Replaying the next answer re-derives the same advance, so the
   * two can never drift apart.
   */
  const live = useMemo(() => {
    if (!battle) return undefined;
    return battle.outcome === 'stage-cleared' && advancedStage === battle.stageIndex
      ? battleReducer(battle, { kind: 'advance' })
      : battle;
  }, [battle, advancedStage]);

  const settled = useRef(false);

  const onGraded = useCallback(
    (result: GradeResult, meta: { responseMs: number; firstTry: boolean; usedHint: boolean }) => {
      if (!def || !live) return;
      // Belt and braces: the runner does not report a malformed answer at all.
      if (result.status === 'malformed') return;
      // The question on screen, which after the first answer is the held one:
      // a second try is still an answer to it, and still real evidence.
      const question = held ?? currentQuestion(live);
      if (!question) return;

      // Real evidence, on the ordinary path — every attempt, retries included,
      // exactly as the practice page records them.
      recordAttempt({
        conceptId: question.conceptId,
        exerciseId: question.exercise.id,
        difficulty: question.exercise.difficulty,
        result,
        responseMs: meta.responseMs,
        usedHint: meta.usedHint,
        firstTry: meta.firstTry,
      });

      /*
       * The boss sees the first answer only. A second try is worth having — the
       * explanation is the point of it — but the dragon already watched the
       * first one, and a fight scored on the last of several tries would not be
       * an assessment of anything.
       */
      if (!meta.firstTry) return;
      recordBattleAnswer({ battleId: def.id, attemptIndex, answer: result.correct ? 'correct' : 'wrong' });
      setHeld(question);
    },
    [attemptIndex, def, held, live, recordAttempt, recordBattleAnswer],
  );

  const pressOn = useCallback(() => setHeld(null), []);

  const onRetry = useCallback(() => {
    if (!def) return;
    resetBattle(def.id);
    setHeld(null);
    setAdvancedStage(null);
    setReading(false);
    // The next win is a re-fight, whatever this one was: the reward has been
    // paid, so the screen must not offer it again.
    setFirstClearHere(false);
    settled.current = false;
  }, [def, resetBattle]);

  /*
   * Settling the win.
   *
   * In an effect rather than in the render, because it writes to the profile;
   * once, because `settled` says so; and paid once beyond that, because
   * `settleBossVictory` claims under `boss:<id>` and a claim is idempotent. A
   * boss fought again for the fun of it is warm and pays nothing — no gold, no
   * XP, and no confetti, which is the part that keeps confetti meaning
   * something (`CLAUDE.md` — celebration is rationed).
   */
  const won = battle?.outcome === 'won' && held === null;
  useEffect(() => {
    if (!def || !profile || !battle || !won || settled.current) return;
    settled.current = true;
    // Read *before* settling: after the dispatch the boss is cleared either
    // way, so this is the only moment that can tell a first clear from a
    // re-fight. A retry in the same visit resets `settled` and asks again —
    // and by then the profile says cleared, so the confetti does not repeat.
    const first = !bossCleared(def, profile);
    settleBossVictory(def, battle);
    if (!first) return;
    setFirstClearHere(true);
    celebrate({
      key: `boss-${def.id}`,
      tone: 'quest',
      icon: def.emoji,
      title: `${def.name} defeated`,
      detail: `+${def.reward.gold} gold`,
      xp: def.reward.xp,
    });
  }, [battle, celebrate, def, profile, settleBossVictory, won]);

  if (!def) return <NotFoundPage />;
  if (!profile || !battle || !live) return <div />;

  const kingdom = KINGDOMS.find((candidate) => candidate.id === def.kingdomId);

  /*
   * The boss ceremony is the first thing a seal has ever withheld, and it still
   * is. A sealed island hands back to the kingdom's own page, which states the
   * seal as mathematics and links the concepts that break it — every one of
   * which opens its lesson and its practice exactly as before.
   */
  if (kingdomSealed(profile, def.kingdomId, Date.now())) {
    return <Navigate to={`/adventure/kingdoms/${def.kingdomId}`} replace />;
  }

  const cleared = bossCleared(def, profile);

  /* ----------------------------------------------------------- the story --- */

  if (reading && battle.outcome === 'fighting') {
    return (
      <div className="page page--narrow boss-page">
        <BossBreadcrumb def={def} kingdomName={kingdom?.name} />
        <BossHeader def={def} />

        <section className="boss-story" aria-labelledby="boss-story-heading">
          <h2 id="boss-story-heading" className="boss-story__heading">
            Before the fight
          </h2>
          <ol className="boss-story__beats">
            {def.story.intro.map((line, index) => (
              <li key={index} className="boss-story__beat">
                {line}
              </li>
            ))}
          </ol>
        </section>

        <div className="button-row">
          <Button size="lg" onClick={() => setReading(false)}>
            Begin the fight
          </Button>
          {kingdom ? (
            <Link to={`/adventure/kingdoms/${kingdom.id}`} className="btn btn--ghost">
              Not yet — back to {kingdom.name}
            </Link>
          ) : null}
        </div>

        <p className="boss-page__note">
          Every question here is the kingdom&rsquo;s own mathematics, from the same bank as your practice pages.
          Nothing is timed, and losing costs you nothing at all.
        </p>
      </div>
    );
  }

  /* --------------------------------------------------------- the victory --- */

  if (battle.outcome === 'won' && held === null) {
    /*
     * Where the win changed something. A kingdom whose seal named this boss is
     * the headline; failing that it is this kingdom, which the win has just
     * turned from open to cleared. The map is told which one to point at.
     */
    const opened = KINGDOMS.find((candidate) => candidate.unlock.clearedBossIds.includes(def.id));
    const changed = opened ?? kingdom;

    return (
      <div className="page page--narrow boss-page">
        {firstClearHere ? <Confetti /> : null}
        <BossBreadcrumb def={def} kingdomName={kingdom?.name} />

        <div className="boss-outcome boss-outcome--won">
          <Mascot mood="celebrate" size={96} />
          <h1 className="page__title">
            <span aria-hidden="true">{def.emoji}</span> {def.name} is beaten
          </h1>
        </div>

        <ol className="boss-story__beats">
          {def.story.victory.map((line, index) => (
            <li key={index} className="boss-story__beat">
              {line}
            </li>
          ))}
        </ol>

        <Card className="boss-reward">
          {firstClearHere ? (
            <p className="boss-reward__paid">
              <span aria-hidden="true">🎁</span> First clear — you were paid <strong>+{def.reward.xp} XP</strong> and{' '}
              <strong>+{def.reward.gold} gold</strong>.
            </p>
          ) : (
            <p className="boss-reward__again">
              <span aria-hidden="true">👑</span> Beaten again. The reward was paid the first time and is not paid
              twice — this one was for the mathematics, and the mathematics still counted: every answer moved your
              mastery.
            </p>
          )}
          <p className="boss-reward__score">
            {battle.stages.map((stage) => `${stage.name} ${stage.correct}/${stage.questions.length}`).join(' · ')}
          </p>
        </Card>

        <div className="button-row">
          <Link
            to={changed ? `/adventure/map?changed=${changed.id}` : '/adventure/map'}
            className="btn btn--primary btn--lg"
          >
            Return to the map
          </Link>
          {kingdom ? (
            <Link to={`/adventure/kingdoms/${kingdom.id}`} className="btn btn--secondary">
              Back to {kingdom.name}
            </Link>
          ) : null}
          <Button variant="ghost" onClick={onRetry}>
            Fight it again
          </Button>
        </div>

        {changed ? (
          <p className="boss-page__note">
            {changed === kingdom
              ? `${changed.name} is marked cleared on the world map now.`
              : `${changed.name} was waiting on this fight — its story is open on the world map now.`}
          </p>
        ) : null}
      </div>
    );
  }

  /* ---------------------------------------------------------- the defeat --- */

  if (battle.outcome === 'lost' && held === null) {
    const review = conceptsToReview(battle);

    return (
      <div className="page page--narrow boss-page">
        <BossBreadcrumb def={def} kingdomName={kingdom?.name} />

        <div className="boss-outcome boss-outcome--lost">
          {/* Thoughtful, never sad: a fight that found the edge of what somebody
              knows has done its job, and a miserable face at the end of it is
              the fastest way to make a child stop coming back. */}
          <Mascot mood="thinking" size={96} />
          <h1 className="page__title">
            {def.name} holds the gate&hellip; <span className="boss-outcome__for-now">for now</span>
          </h1>
        </div>

        <ol className="boss-story__beats">
          {def.story.defeat.map((line, index) => (
            <li key={index} className="boss-story__beat">
              {line}
            </li>
          ))}
        </ol>

        <Card className="boss-weakness">
          <h2 className="boss-weakness__heading">
            <span aria-hidden="true">🔎</span> You found a weakness
          </h2>
          {review.length > 0 ? (
            <>
              <p className="boss-weakness__lead">
                {review.length === 1 ? 'This is the idea' : `These are the ${review.length} ideas`} the wrong answers
                touched. That is worth more than the gold, and it cost you nothing.
              </p>
              <ul className="boss-weakness__list">
                {review.map((conceptId) => {
                  const concept = registry.concept(conceptId);
                  if (!concept) return null;
                  return (
                    <li key={conceptId} className="boss-weakness__item">
                      <div className="boss-weakness__body">
                        <Link to={`/concepts/${conceptId}`} className="boss-weakness__name">
                          {concept.title}
                        </Link>
                        <MasteryBand level={masteryOf(conceptId).level} />
                      </div>
                      <Link to={`/practice/${conceptId}`} className="btn btn--sky btn--sm">
                        Practise
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="boss-weakness__lead">
              Nothing went wrong that a name can be put to — the stage simply ran out of questions. Go again whenever
              you like.
            </p>
          )}
        </Card>

        <div className="button-row">
          <Button size="lg" onClick={onRetry}>
            Fight again
          </Button>
          {kingdom ? (
            <Link to={`/adventure/kingdoms/${kingdom.id}`} className="btn btn--secondary">
              Back to {kingdom.name}
            </Link>
          ) : null}
        </div>

        <p className="boss-page__note">
          Nothing was lost: no gold, no streak, no progress. The next attempt asks different questions, so it is a real
          fight rather than the same four answers again.
        </p>
      </div>
    );
  }

  /* ------------------------------------------------------- between stages --- */

  const paused = live.outcome === 'stage-cleared';
  if (paused && held === null) {
    const stage = live.stages[live.stageIndex];
    const next = live.stages[live.stageIndex + 1];
    return (
      <div className="page page--narrow boss-page">
        <BossBreadcrumb def={def} kingdomName={kingdom?.name} />
        <BossHeader def={def} />

        <Card className="boss-interlude">
          <h2 className="boss-interlude__heading">
            <span aria-hidden="true">✓</span> {stage?.name} cleared
          </h2>
          <p className="boss-interlude__score">
            {stage?.correct} of {stage?.questions.length} right.
          </p>
          {next ? (
            <p className="boss-interlude__next">
              Next: <strong>{next.name}</strong> — {next.questions.length} questions at difficulty{' '}
              {next.difficulty} of 5.
            </p>
          ) : null}
          <Button size="lg" onClick={() => setAdvancedStage(live.stageIndex)}>
            Press on
          </Button>
        </Card>
      </div>
    );
  }

  /* ----------------------------------------------------------- the fight --- */

  const question: BattleQuestion | undefined = currentQuestion(live);
  const stage = live.stages[live.stageIndex];
  const exercise = held?.exercise ?? question?.exercise;

  if (!stage || !exercise) return <div />;

  /*
   * The dots read the profile's own flat answer list, sliced to this stage.
   * The battle itself keeps only counts — which is all the *scoring* needs —
   * and the per-question history is exactly what was persisted, so the dots
   * come back after a reload saying what they said before it.
   */
  const before = live.stages.slice(0, live.stageIndex).reduce((sum, entry) => sum + entry.questions.length, 0);
  const outcomes: DotOutcome[] = stage.questions.map((_question, index) => answers[before + index] ?? 'pending');

  return (
    <div className="page page--narrow boss-page">
      <BossBreadcrumb def={def} kingdomName={kingdom?.name} />
      <BossHeader def={def} cleared={cleared} />

      <header className="boss-stage">
        <p className="boss-stage__label">
          Stage {live.stageIndex + 1} of {live.stages.length} · difficulty {stage.difficulty} of 5
        </p>
        <h2 className="boss-stage__name">{stage.name}</h2>
        <StageDots outcomes={outcomes} />
      </header>

      {/*
       * The ordinary runner, unchanged. It knows how to ask a question, grade
       * it, explain it and offer the next one — and forking it for a boss would
       * create a second place answers are graded, which within a release would
       * disagree with the first about something.
       */}
      <ExerciseRunner exercise={exercise} onGraded={onGraded} showNext onNext={pressOn} />

      <div className="boss-page__footer">
        {kingdom ? (
          <Link to={`/adventure/kingdoms/${kingdom.id}`} className="btn btn--ghost btn--sm">
            Leave the fight
          </Link>
        ) : null}
        <p className="boss-page__note">
          Walking away costs nothing and keeps your place — this fight will be exactly where you left it.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Furniture                                                                  */
/* -------------------------------------------------------------------------- */

function BossBreadcrumb({ def, kingdomName }: { def: BossDef; kingdomName?: string }): JSX.Element {
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <Link to="/adventure/map">World map</Link>
      <span aria-hidden="true">/</span>
      {kingdomName ? <Link to={`/adventure/kingdoms/${def.kingdomId}`}>{kingdomName}</Link> : <span>Boss</span>}
      <span aria-hidden="true">/</span>
      <span>{def.name}</span>
    </nav>
  );
}

function BossHeader({ def, cleared = false }: { def: BossDef; cleared?: boolean }): JSX.Element {
  return (
    <header className="boss-header">
      <span className="boss-header__emoji" aria-hidden="true">
        {def.emoji}
      </span>
      <div>
        <h1 className="page__title">{def.name}</h1>
        <p className="boss-header__meta">
          {def.stages.length} {def.stages.length === 1 ? 'stage' : 'stages'} ·{' '}
          {def.stages.reduce((sum, stage) => sum + stage.questionCount, 0)} questions · no timer
          {cleared ? ' · already beaten' : ''}
        </p>
      </div>
    </header>
  );
}
